/**
 * DoesTheDogDie.com API integration
 * Provides community-sourced content warnings for movies.
 *
 * In dev, requests are proxied through Vite (see vite.config.js)
 * to avoid CORS issues. The API key is injected by the proxy.
 * In production, you'll need a serverless function or Cloudflare Worker
 * to proxy these requests.
 */
const BASE = '/api/dtdd'

const headers = {
  Accept: 'application/json',
}

/**
 * Search DTDD for a movie by title, returning the best match's item ID.
 * Optionally pass tmdbId to match exactly.
 */
export async function findDTDDMovie(title, tmdbId) {
  try {
    const res = await fetch(`${BASE}/dddsearch?q=${encodeURIComponent(title)}`, { headers })
    if (!res.ok) return null
    const data = await res.json()
    const items = data.items || []

    // Try exact TMDB match first
    if (tmdbId) {
      const exact = items.find(i => i.tmdbid === tmdbId && i.itemType?.name === 'Movie')
      if (exact) return exact.id
    }

    // Fall back to first movie result
    const movie = items.find(i => i.itemType?.name === 'Movie')
    return movie?.id || null
  } catch {
    return null
  }
}

/**
 * Get content warnings for a DTDD item ID.
 * Returns only warnings where the community voted "yes" (more yes than no),
 * sorted by vote confidence.
 */
export async function getContentWarnings(dtddId) {
  if (!dtddId) return []
  try {
    const res = await fetch(`${BASE}/media/${dtddId}`, { headers })
    if (!res.ok) return []
    const data = await res.json()

    const stats = data.topicItemStats || []

    return stats
      .filter(s => s.yesSum > 0 && s.yesSum > s.noSum) // community says yes
      .map(s => ({
        name: s.topic?.name || '?',
        yesCount: s.yesSum || 0,
        noCount: s.noSum || 0,
        isSensitive: s.topic?.isSensitive || false,
      }))
      .sort((a, b) => b.yesCount - a.yesCount)
  } catch {
    return []
  }
}

/**
 * All-in-one: search + get warnings for a movie.
 */
export async function getWarningsForMovie(title, tmdbId) {
  const itemId = await findDTDDMovie(title, tmdbId)
  if (!itemId) return null
  const warnings = await getContentWarnings(itemId)
  return { itemId, warnings }
}
